import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { queryClient } from 'App';
import { mutateWithOffline } from 'utils/mutateWithOffline';
import { upsertOfflineMeasurementInstallationInfo } from 'hooks/useOfflineMeasurementInstallationInfo';
import { resolveOfflineUuid } from 'hooks/useOfflineUuidMap';

type InstallationInfoData = {
  measurementUuid: string;
  constructionSiteUuids: Array<{ uuid: string, comment?: string }>;
  theBuildingIsMadeOf: 'ceramicBlock' | 'yTong' | 'silicate' | 'woodenStructure' | 'other';
  theBuildingIsMadeOfComment?: string;
  plannedThickness: string;
  installationElementsUuids: Array<{ uuid: string, comment?: string }>;
  numberOfAssemblyTeams: string;
  plannedAssemblyTime: string;
};

const usePutInstallationInfo = (
  options?: UseMutationOptions<any, Error, InstallationInfoData>
) => {
  return useMutation({
    ...options,
    mutationKey: ['editInstallationInfo'],
    mutationFn: async (data) => {
      return mutateWithOffline<any>(
        'PUT',
        `/measurement/installation-info`,
        data,
        {
          entityType: 'measurement',
          dedupeKey: `measurement:installation-info:${resolveOfflineUuid(data.measurementUuid)}`,
          simulateResponse: ({ tempId }) => {
            const now = new Date().toISOString();

            // Jeśli pomiar zdążył się już zsynchronizować, to w offline możemy mieć mapowanie
            // tempUuid -> realUuid. Zapis w localStorage robimy zawsze pod "realnym" kluczem.
            const effectiveUuid = resolveOfflineUuid(data.measurementUuid);

            upsertOfflineMeasurementInstallationInfo({
              measurementUuid: effectiveUuid,
              theBuildingIsMadeOf: data.theBuildingIsMadeOf,
              theBuildingIsMadeOfComment: data.theBuildingIsMadeOfComment ?? null,
              plannedThickness: data.plannedThickness,
              numberOfAssemblyTeams: data.numberOfAssemblyTeams,
              plannedAssemblyTime: data.plannedAssemblyTime,
              constructionSiteUuids: (data.constructionSiteUuids || []) as any,
              installationElementsUuids: (data.installationElementsUuids || []) as any,
              updatedAt: now,
            });

            return {
              status: 'success',
              message: 'Measurement updated (offline)',
              measurement: {
                uuid: effectiveUuid,
                measurementInstallationInfo: {
                  uuid: tempId,
                  measurementId: null,
                  theBuildingIsMadeOf: data.theBuildingIsMadeOf,
                  plannedThickness: data.plannedThickness,
                  numberOfAssemblyTeams: data.numberOfAssemblyTeams,
                  plannedAssemblyTime: data.plannedAssemblyTime,
                  createdAt: now,
                  updatedAt: now,
                  id: null,
                  constructionSite: [],
                  installationElements: [],
                  constructionSiteUuids: data.constructionSiteUuids,
                  installationElementsUuids: data.installationElementsUuids,
                  theBuildingIsMadeOfComment: data.theBuildingIsMadeOfComment ?? null,
                },
              },
            };
          },
        }
      );
    },
    onMutate: (variables) => {
      Toast({
        message: 'Przetwarzanie danych montażowych...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'edit-installation-info',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      Toast({
        message: 'Dane montażowe zaktualizowane pomyślnie.',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'edit-installation-info',
      });
      queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Błąd podczas aktualizacji danych montażowych.',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'edit-installation-info',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default usePutInstallationInfo;
